/**
 * Post-onboarding device connection prompt.
 * Shows on the dashboard for users who skipped device connection during onboarding.
 * Dismissable — reappears next day, stops after 3 dismissals.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/GlassCard';
import { CardContent } from '@/components/ui/layout/card';
import { Watch, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  trackActivationDevicePromptShown,
  trackActivationDevicePromptClicked,
  trackActivationDevicePromptDismissed,
} from '@/lib/posthog';

const STORAGE_KEY = 'device_prompt_state';
const MAX_DISMISSALS = 3;
const SHOW_FOR_DAYS = 7;

interface PromptState {
  dismissCount: number;
  lastDismissedDate: string | null;
  signupDate: string;
}

function getPromptState(): PromptState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const state: PromptState = {
    dismissCount: 0,
    lastDismissedDate: null,
    signupDate: new Date().toISOString().split('T')[0],
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

function savePromptState(state: PromptState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

interface DeviceConnectionPromptProps {
  /** Whether any device is already connected */
  hasDevice: boolean;
}

export function DeviceConnectionPrompt({ hasDevice }: DeviceConnectionPromptProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<PromptState>(getPromptState);

  useEffect(() => {
    if (hasDevice) {
      setVisible(false);
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const daysSinceSignup = Math.floor(
      (new Date(today).getTime() - new Date(state.signupDate).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Don't show after SHOW_FOR_DAYS or after MAX_DISMISSALS
    if (daysSinceSignup > SHOW_FOR_DAYS || state.dismissCount >= MAX_DISMISSALS) {
      setVisible(false);
      return;
    }

    // Don't show again on the same day it was dismissed
    if (state.lastDismissedDate === today) {
      setVisible(false);
      return;
    }

    setVisible(true);
    trackActivationDevicePromptShown(daysSinceSignup);
  }, [hasDevice, state]);

  const handleDismiss = () => {
    const today = new Date().toISOString().split('T')[0];
    const newState: PromptState = {
      ...state,
      dismissCount: state.dismissCount + 1,
      lastDismissedDate: today,
    };
    setState(newState);
    savePromptState(newState);
    trackActivationDevicePromptDismissed(newState.dismissCount);
    setVisible(false);
  };

  const handleConnect = () => {
    trackActivationDevicePromptClicked('settings');
    navigate('/settings');
  };

  if (!visible) return null;

  return (
    <GlassCard className="border-[#00FFC8]/20">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00FFC8]/10 flex items-center justify-center shrink-0">
            <Watch className="h-5 w-5 text-[#00FFC8]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-mono text-xs uppercase tracking-wider text-[#00FFC8]/90 mb-1">
              {t('dashboard.devicePrompt.title', { defaultValue: 'Connect your watch' })}
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              {t('dashboard.devicePrompt.description', {
                defaultValue: 'Auto-sync workouts and get smarter, data-driven coaching.',
              })}
            </p>
            <Button
              onClick={handleConnect}
              size="sm"
              className={cn(
                'font-mono text-[10px] uppercase tracking-wider',
                'bg-[#00FFC8]/10 border border-[#00FFC8]/30 text-[#00FFC8]',
                'hover:bg-[#00FFC8]/20'
              )}
            >
              Connect Device
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="shrink-0 h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </GlassCard>
  );
}
